import {
    categorizedActionsFragment,
    reviewOrderFragment,
    internalOrderDetailsFragment,
    orderLinkedDentistFragment,
    orderLinkedExtTechFragment,
    orderDetailsFragment,
    orderListFragment,
    orderRowContextFragment,
    stepOrderListFragment,
    patientFragment,
    deliveryDetailsFragment,
    restorationDataFragment,
    scannerCaseListItemFragment,
    customerSettingsFragment,
    orgContactFragment,
} from 'apollo/fragments';
import { filesUrlFragment, restorationFragment } from 'apollo/fragments/order';
import { orgCurrentJobFragment } from 'apollo/fragments/organization';
import { gql } from 'graphql.macro';

export const getCategorizedActionsTemplate = gql`
    query categorizedActions($clinicId: MongoDbObjectId!, $orderId: MongoDbObjectId) {
        categorizedActions(clinicId: $clinicId, orderId: $orderId) {
            ...categorizedActionsFields
        }
    }
    ${categorizedActionsFragment}
`;

export const reviewOrdersTemplate = gql`
    query reviewOrders(
        $filters: ReviewOrdersFilters
        $limit: Int
        $offset: Int
        $orderBy: String
        $orderDirection: OrderDirection
    ) {
        reviewOrders(
            filters: $filters
            limit: $limit
            offset: $offset
            orderBy: $orderBy
            orderDirection: $orderDirection
        ) {
            orders {
                ...reviewOrderFields
            }
        }
    }
    ${reviewOrderFragment}
`;

export const getOrderListItemsTemplate = gql`
    query OrderListItems(
        $step: MongoDbObjectId
        $filters: OrderFilter
        $limit: Int
        $offset: Int
        $orderBy: String
        $orderDirection: OrderDirection
        $listType: String
    ) {
        orderListItems(
            step: $step
            filters: $filters
            limit: $limit
            offset: $offset
            orderBy: $orderBy
            orderDirection: $orderDirection
            listType: $listType
        ) {
            _id
            labFlag
            isInternalOrderData
            type
            status
            invoicingState
            isProductionFinished
            orderId
            internalOrderId
            unreadMessagesCount
            shouldShowTruckButton
            supplier {
                _id
                name
            }
            customer {
                _id
                name
                type
            }
            dentist {
                _id
                fullName
            }
            orderTags {
                _id
                name
                color
            }
            patient {
                firstName
                lastName
                personalIdentityNumber
            }
            restorationTypes
            constructionMaterials
            created {
                on
                by {
                    _id
                    fullName
                }
            }
            deliveryDetails {
                pickUpLocation {
                    _id
                    name
                }
                destination {
                    _id
                    name
                }
                isPickUpAtClinic
                isLabHandleRequest
                isLabHaveOwnResource
                sampleType
                price
                date
                status
                deadlineStatus
                internalDeliveryDate
                externalDeliveryDate
            }
            workFlow {
                _id
                timeInCurrentStep {
                    hours
                    minutes
                    seconds
                }
                currentStep {
                    _id
                    stepId
                    name
                    dateTime
                    isPaused
                    maxHourInStep
                    workStarted
                    user {
                        _id
                        fullName
                    }
                }
            }
            isModify
            linkedDentists {
                user {
                    _id
                }
                dateAdded
            }
            linkedExtTechs {
                user {
                    _id
                }
                dateAdded
            }
            invoice {
                _id
                status
            }
            hasOrderFiles
            archived {
                by
                byCustomer
                byLab
            }
            isImportedOrder
            hasDeliveryNotePdf
        }
    }
`;

export const getOrdersForTechnicianTemplate = gql`
    query ordersForTechnician(
        $filters: OrderFilter
        $limit: Int
        $offset: Int
        $orderBy: String
        $orderDirection: OrderDirection
        $currentLabId: String
    ) {
        ordersForTechnician(
            filters: $filters
            limit: $limit
            offset: $offset
            orderBy: $orderBy
            orderDirection: $orderDirection
            currentLabId: $currentLabId
        ) {
            ...orderListFields
        }
    }
    ${orderListFragment}
`;

export const getOrdersForDentistTemplate = gql`
    query ordersForDentist(
        $filters: OrderFilter
        $limit: Int
        $offset: Int
        $orderBy: String
        $orderDirection: OrderDirection
        $currentLabId: String
    ) {
        ordersForDentist(
            filters: $filters
            limit: $limit
            offset: $offset
            orderBy: $orderBy
            orderDirection: $orderDirection
            currentLabId: $currentLabId
        ) {
            ...orderListFields
        }
    }
    ${orderListFragment}
`;

export const getSupplierOrdersTemplate = gql`
    query supplierOrders(
        $filters: OrderFilter
        $limit: Int
        $offset: Int
        $orderBy: String
        $orderDirection: OrderDirection
        $currentLabId: String
    ) {
        supplierOrders(
            filters: $filters
            limit: $limit
            offset: $offset
            orderBy: $orderBy
            orderDirection: $orderDirection
            currentLabId: $currentLabId
        ) {
            ...orderListFields
        }
    }
    ${orderListFragment}
`;

export const getStepOrdersTemplate = gql`
    query stepOrders(
        $step: String
        $filters: OrderFilter
        $limit: Int
        $offset: Int
        $orderBy: String
        $orderDirection: OrderDirection
    ) {
        stepOrders(
            step: $step
            filters: $filters
            limit: $limit
            offset: $offset
            orderBy: $orderBy
            orderDirection: $orderDirection
        ) {
            ...stepOrderListFields
        }
    }
    ${stepOrderListFragment}
`;

// DD-3231: refresh a single row of the order lists after an ORDER_CHANGED socket event,
// with the same selection the list itself uses, so the normalized Order updates in place.
export const getOrderListRowTemplate = gql`
    query orderListRow($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            ...orderListFields
        }
    }
    ${orderListFragment}
`;

export const getStepOrderListRowTemplate = gql`
    query stepOrderListRow($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            ...stepOrderListFields
        }
    }
    ${stepOrderListFragment}
`;

export const getUnCertifiedOrdersTemplate = gql`
    query uncertifiedOrders($filters: UnCertifiedOrderFilter, $limit: Int, $offset: Int) {
        uncertifiedOrders(filters: $filters, limit: $limit, offset: $offset) {
            _id
            orderId
            currency
            type
            status
            deliveryDetails {
                sampleType
            }
            supplier {
                _id
                name
            }
            customer {
                _id
                name
            }
            patient {
                firstName
                lastName
                personalIdentityNumber
            }
            restorationTypes
            isCertified
            # DD-2506: the price rows the certify dialog lists
            restorations {
                _id
                actions {
                    _id
                    code
                    name
                    price {
                        quantity
                        unitPrice
                        vatPercent
                        totalWithoutVat
                        totalWithVat
                    }
                }
                materials {
                    _id
                    code
                    name
                    price {
                        quantity
                        unitPrice
                        vatPercent
                        totalWithoutVat
                        totalWithVat
                    }
                }
            }
            created {
                on
                by {
                    _id
                    fullName
                }
            }
            totalAmount {
                totalPrice
                vatAmount
            }
        }
    }
`;

export const getDraftOrdersTemplate = gql`
    query draftOrders($filters: DraftOrderFilter, $limit: Int, $offset: Int) {
        draftOrders(filters: $filters, limit: $limit, offset: $offset) {
            _id
            orderId
            status
            draftCreatedAt
            draftUpdatedAt
            draftCreatedBy {
                _id
                fullName
            }
            dentist {
                _id
                fullName
            }
            patient {
                firstName
                lastName
                personalIdentityNumber
            }
            supplier {
                _id
                name
            }
            # DD-2506: drafts written since step 3 carry only their rows
            restorations {
                _id
                actions {
                    code
                }
            }
        }
    }
`;

export const getOrderDetailsTemplate = gql`
    query order($orderId: MongoDbObjectId!, $version: Int) {
        order(orderId: $orderId, version: $version) {
            ...orderDetailsFields
        }
    }
    ${orderDetailsFragment}
`;

export const getOrderEventsTemplate = gql`
    query orderEvents($orderIds: [MongoDbObjectId!]!, $filters: OrderEventFilters) {
        orderEvents(orderIds: $orderIds, filters: $filters) {
            id
            date
            type
            orderId
            orderDbId
            user {
                _id
                name {
                    firstName
                    lastName
                }
                fullName
                organization {
                    _id
                    name
                    type
                }
            }
            color
            message
            lineItems
            files {
                _id
                filename
                url
                mimetype
            }
        }
    }
`;

export const getStatusLogsTemplate = gql`
    query statusLogs($orderId: MongoDbObjectId!) {
        statusLogs(orderId: $orderId) {
            message
            date
            user {
                fullName
            }
            manualSelectedDate
        }
    }
`;

export const commentsTemplate = gql`
    query comments($orderId: MongoDbObjectId!) {
        comments(orderId: $orderId) {
            message
            date
            user {
                fullName
            }
            version
        }
    }
`;

export const getInternalOrderDetailsTemplate = gql`
    query internalOrderDetails($orderId: MongoDbObjectId!) {
        internalOrderDetails(orderId: $orderId) {
            ...internalOrderDetailsFields
        }
    }
    ${internalOrderDetailsFragment}
`;

export const getInternalOrderNotesTemplate = gql`
    query internalOrderNotes($orderId: MongoDbObjectId!) {
        internalOrderDetails(orderId: $orderId) {
            _id
            notes
        }
    }
`;

export const getOrderForLinkedDentistTemplate = gql`
    query order($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            ...orderLinkedDentistFields
        }
    }
    ${orderLinkedDentistFragment}
`;

export const getOrderForLinkedExtTechTemplate = gql`
    query order($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            ...orderLinkedExtTechFields
        }
    }
    ${orderLinkedExtTechFragment}
`;

export const getSamePatientOrdersTemplate = gql`
    query samePatientOrders($personalIdentityNumber: String!) {
        samePatientOrders(personalIdentityNumber: $personalIdentityNumber) {
            _id
            type
            status
            dentist {
                _id
                fullName
            }
            patient {
                firstName
                lastName
                personalIdentityNumber
            }
            deliveryDetails {
                date
            }
            restorationTypes
            created {
                on
            }
            originalCreationDate
        }
    }
`;

export const getPatientByPINTemplate = gql`
    query patientByPIN($personalIdentityNumber: String!) {
        patientByPIN(personalIdentityNumber: $personalIdentityNumber) {
            _id
            firstName
            lastName
            personalIdentityNumber
            createdOn
        }
    }
`;

// DD-3484 Marketplace: everything the subcontract flow needs from the parent order —
// the restorations, attachments and scanner-case files to select from, and the patient
// reference and dentist whose travel is decided per order, and the delivery details
// that pre-fill the sub-order's delivery date (DD-3689). The parent's communication
// is read separately (subcontract-communication.graphql.ts).
export const orderForSubcontractTemplate = gql`
    query order($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            _id
            orderId
            type
            status
            rejectStatus
            isDraft
            additionalInfo
            deliveryDetails {
                date
                externalDeliveryDate
                destination {
                    _id
                }
                sample {
                    type
                }
            }
            patient {
                firstName
                lastName
                personalIdentityNumber
                noPersonalIdentityNumber
            }
            dentist {
                _id
                fullName
            }
            restorations {
                ...restorationFields
            }
            filesUrl {
                ...filesUrlFields
            }
            attachedScannerCaseFiles {
                _id
                displayCaseId
                processedFiles {
                    _id
                    filename
                    mimetype
                    url
                }
            }
        }
    }
    ${restorationFragment}
    ${filesUrlFragment}
`;

export const orderForSimpleModifyTemplate = gql`
    query order($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            _id
            restorations {
                ...restorationFields
            }
            orderId
            type
            # DD-3484: resolves only for the lab that created the sub-order; the wizard
            # shows a banner so a curated sub-order is not edited obliviously.
            parentOrder {
                _id
                orderId
            }
            additionalInfo
            historicalComments {
                additionalInfo
                createdAt
                updatedBy
                version
            }
            patient {
                ...patientFields
            }
            supplier {
                _id
                name
                openingHours {
                    day
                    time {
                        from
                        to
                    }
                    active
                }
                holidayDays
                currentJobs {
                    ...orgCurrentJobFields
                }
                customerSettings {
                    ...customerSettingsFields
                }
            }
            customer {
                _id
                name
                type
            }
            dentist {
                _id
                name {
                    firstName
                    lastName
                }
                email
                fullName
                local
            }
            deliveryDetails {
                ...deliveryDetailsFields
            }
            isWarrantyOrder
            isCopyOrder
            isDraft
            linkedDentists {
                user {
                    _id
                    fullName
                    email
                }
                dateAdded
            }
            linkedClinics {
                clinic {
                    _id
                    name
                    contact {
                        communication {
                            email
                        }
                    }
                }
                dateAdded
            }
            customParticipantEmails
            filesUrl {
                ...filesUrlFields
            }
            deliveryDetails {
                sample {
                    type
                    content {
                        name
                        value
                    }
                    otherContent
                }
            }
        }
    }
    ${patientFragment}
    ${deliveryDetailsFragment}
    ${filesUrlFragment}
    ${orgCurrentJobFragment}
    ${customerSettingsFragment}
    ${restorationFragment}
`;

export const aiRestorationsTemplate = gql`
    query aiRestorations($promt: String!) {
        aiRestorations(promt: $promt) {
            ...restorationDataFields
        }
    }
    ${restorationDataFragment}
`;

export const orderForLabModifyTemplate = gql`
    query order($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            _id
            restorations {
                ...restorationFields
            }
            orderId
            currency
            type
            additionalInfo
            historicalComments {
                additionalInfo
                createdAt
                updatedBy
                version
            }
            patient {
                ...patientFields
            }
            supplier {
                _id
                name
            }
            customer {
                _id
                name
                orderVersion
            }
            dentist {
                _id
                name {
                    firstName
                    lastName
                }
                email
                fullName
                local
            }
            deliveryDetails {
                ...deliveryDetailsFields
            }
            isWarrantyOrder
            isCopyOrder
            isDraft
            isCreatedByLab
            filesUrl {
                ...filesUrlFields
            }
            deliveryDetails {
                sample {
                    type
                    content {
                        name
                        value
                    }
                    otherContent
                }
            }
            manualSelectedReceivedDate
            isDraft
            panNumber
            panNumberDescription
            internalOrderId
            productionPlan {
                stepId
                stepName
                technicianId
                technicianName
                deadline
                estimatedProductionHours
                estimatedMachineHours
                stepOrder
                workUnits
                departmentId
                machineId
            }
        }
    }
    ${patientFragment}
    ${deliveryDetailsFragment}
    ${filesUrlFragment}
    ${restorationFragment}
`;

export const getDentistOrdersTemplate = gql`
    query dentistOrders($dentistId: MongoDbObjectId!) {
        dentistOrders(dentistId: $dentistId) {
            _id
            orderId
            created {
                on
            }
            originalCreationDate
            customer {
                _id
                name
            }
            deliveryDetails {
                destination {
                    _id
                    name
                }
            }
        }
    }
`;

export const getLabsWithRankingsTemplate = gql`
    query labsWithRankings(
        $isModifyOrWarrantyFlow: Boolean!
        $clinicId: MongoDbObjectId!
        $restorations: [RestorationInput!]!
        $orderId: MongoDbObjectId
        $selectedLabId: MongoDbObjectId
    ) {
        labsWithRankings(
            isModifyOrWarrantyFlow: $isModifyOrWarrantyFlow
            clinicId: $clinicId
            restorations: $restorations
            orderId: $orderId
            selectedLabId: $selectedLabId
        ) {
            labId
            holidayDays
            openingHours {
                day
                time {
                    from
                    to
                }
                active
            }
            name
            profileImage
            # DD-2506: the selection priced for this lab exactly as a save would store it
            restorations {
                _id
                actions {
                    _id
                    actionId
                    code
                    name
                    quotable
                    isTioTandvard
                    price {
                        quantity
                        unitPrice
                        vatPercent
                        totalWithoutVat
                        totalWithVat
                    }
                }
                materials {
                    _id
                    name
                    code
                    isMaterialSurcharge
                    isTioTandvard
                    price {
                        quantity
                        unitPrice
                        vatPercent
                        totalWithoutVat
                        totalWithVat
                    }
                }
            }
            totalPrice
            totalVat
            ratePercent
            totalRating
            rank
            currency
            isRequireException
            labExceptions
            isQuotable
            deliveryDate {
                deliveryDate
                isDeliveryDateAHoliday
            }
            contact {
                ...orgContactFields
            }
            longDescription
            unavailableLabDates
        }
    }
    ${orgContactFragment}
`;

export const getRecentlyUsedLabsWithRankingsTemplate = gql`
    query recentlyUsedLabsWithRankings($restorations: [RestorationInput!]!) {
        recentlyUsedLabsWithRankings(restorations: $restorations) {
            labId
            holidayDays
            openingHours {
                day
                time {
                    from
                    to
                }
                active
            }
            name
            profileImage
            # DD-2506: the selection priced for this lab exactly as a save would store it
            restorations {
                _id
                actions {
                    _id
                    actionId
                    code
                    name
                    quotable
                    isTioTandvard
                    price {
                        quantity
                        unitPrice
                        vatPercent
                        totalWithoutVat
                        totalWithVat
                    }
                }
                materials {
                    _id
                    name
                    code
                    isMaterialSurcharge
                    isTioTandvard
                    price {
                        quantity
                        unitPrice
                        vatPercent
                        totalWithoutVat
                        totalWithVat
                    }
                }
            }
            totalPrice
            totalVat
            ratePercent
            totalRating
            rank
            currency
            isRequireException
            labExceptions
            isQuotable
            deliveryDate {
                deliveryDate
                isDeliveryDateAHoliday
            }
            contact {
                ...orgContactFields
            }
            longDescription
            unavailableLabDates
        }
    }
    ${orgContactFragment}
`;

export const orderForActionModifyTemplate = gql`
    query order($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            _id
            restorations {
                ...restorationFields
            }
            currency
            orderId
            type
            additionalInfo
            historicalComments {
                additionalInfo
                createdAt
                updatedBy
                version
            }
            patient {
                ...patientFields
            }
            supplier {
                _id
                name
            }
            customer {
                _id
                name
                type
                activeActionSet
                customerSettings {
                    _id
                    name
                    value
                }
            }
            dentist {
                _id
                name {
                    firstName
                    lastName
                }
                email
                fullName
                local
            }
            deliveryDetails {
                ...deliveryDetailsFields
            }
            isWarrantyOrder
            isCopyOrder
            isDraft
            linkedDentists {
                user {
                    _id
                    fullName
                    email
                }
                dateAdded
            }
            linkedClinics {
                clinic {
                    _id
                    name
                    contact {
                        communication {
                            email
                        }
                    }
                }
                dateAdded
            }
            customParticipantEmails
            filesUrl {
                ...filesUrlFields
            }
            actionSet
            attachedScannerCaseFiles {
                ...scannerCaseListItemFields
            }
        }
    }
    ${patientFragment}
    ${deliveryDetailsFragment}
    ${filesUrlFragment}
    ${scannerCaseListItemFragment}
    ${restorationFragment}
`;

export const getOrderPriceSpecTemplate = gql`
    query order($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            _id
            # DD-2506: the order's price rows
            restorations {
                _id
                actions {
                    _id
                    code
                    name
                    quotable
                    isTioTandvard
                    price {
                        quantity
                        unitPrice
                        vatPercent
                        totalWithoutVat
                        totalWithVat
                    }
                }
                materials {
                    _id
                    name
                    isTioTandvard
                    price {
                        quantity
                        unitPrice
                        vatPercent
                        totalWithoutVat
                        totalWithVat
                    }
                }
            }
            currency
        }
    }
`;

export const getProductionValueReportsTemplate = gql`
    query ProductionValueReports($orderId: MongoDbObjectId!, $actions: [ProdActionInput]) {
        productionValueReports(orderId: $orderId, actions: $actions) {
            actions {
                actionId
                name
                number
                productionDeductionValue
            }
            templateName
            steps {
                step
                department
                technician
                technicians {
                    _id
                    fullName
                }
                valuePercent
            }
        }
    }
`;

export const autoScheduleProductionPlanTemplate = gql`
    query autoScheduleProductionPlan($input: AutoScheduleInput!, $excludeOrderId: ID) {
        autoScheduleProductionPlan(input: $input, excludeOrderId: $excludeOrderId) {
            stepId
            startDate
            deadline
            dayNumber
        }
    }
`;

export const productionDashboardDataTemplate = gql`
    query productionDashboardData($fromDate: Date!, $toDate: Date!) {
        productionDashboardData(fromDate: $fromDate, toDate: $toDate) {
            orderId
            orderDisplayId
            stepId
            stepName
            currentStepName
            workStarted
            isPaused
            pauseReason
            lastActionByName
            lastActionType
            department
            technicianName
            startDate
            deadline
            scheduleStatus
            estimatedProductionHours
            clinicName
            deliveryClinicName
            dentistName
            patientName
        }
    }
`;

export const calculateStepHoursTemplate = gql`
    query calculateStepHours($input: CalculateStepHoursInput!) {
        calculateStepHours(input: $input) {
            stepId
            estimatedProductionHours
        }
    }
`;

export const updateStepWithCascadeTemplate = gql`
    query updateStepWithCascade($input: UpdateStepWithCascadeInput!, $excludeOrderId: ID) {
        updateStepWithCascade(input: $input, excludeOrderId: $excludeOrderId) {
            stepId
            startDate
            deadline
            dayNumber
            estimatedProductionHours
        }
    }
`;

export const departmentCapacityTemplate = gql`
    query departmentCapacity($excludeOrderId: ID) {
        departmentCapacity(excludeOrderId: $excludeOrderId) {
            department
            date
            totalHours
        }
    }
`;

export const planOverviewTemplate = gql`
    query planOverview($steps: [ProductionPlanStepInput!], $orderId: ID) {
        planOverview(steps: $steps, orderId: $orderId) {
            hourSlots {
                hour
                dayLabel
                timestamp
                isWorkingHour
                machineRunNames
            }
            departmentGroups {
                departmentName
                steps {
                    stepId
                    stepName
                    departmentName
                    color
                    startSlotIndex
                    endSlotIndex
                    startTime
                    endTime
                    productionEndSlotIndex
                    machineStartSlotIndex
                    extendsBeforeView
                    extendsAfterView
                }
            }
            orderDisplayId
            clinicName
            patientName
            currentHourSlotIndex
        }
    }
`;

export const ordersByIdsTemplate = gql`
    query ordersByIds($ids: [MongoDbObjectId!]!) {
        ordersByIds(ids: $ids) {
            _id
            orderId
            patient {
                firstName
                lastName
            }
            customer {
                name
            }
        }
    }
`;

export const getOrderForRowActionTemplate = gql`
    query orderForRowAction($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            ...orderRowContextFields
            # DD-2506: the received-by-lab dialog matches production templates on these
            restorations {
                _id
                actions {
                    actionId
                    price {
                        quantity
                    }
                }
            }
        }
    }
    ${orderRowContextFragment}
`;

export const getOrderFilesUrlTemplate = gql`
    query orderFilesUrl($orderId: MongoDbObjectId!) {
        order(orderId: $orderId) {
            _id
            filesUrl {
                ...filesUrlFields
            }
        }
    }
    ${filesUrlFragment}
`;

// DD-3463: what a batch action would do with the selected orders, before it runs.
export const batchSendOrdersPreviewTemplate = gql`
    query batchSendOrdersPreview($orderIds: [MongoDbObjectId!]!) {
        batchSendOrdersPreview(orderIds: $orderIds) {
            eligible {
                _id
                orderId
                invoiceGroup
            }
            skipped {
                _id
                orderId
                reason
            }
            isPerOrderInvoiceDisabled
        }
    }
`;

export const batchProductionFinishedPreviewTemplate = gql`
    query batchProductionFinishedPreview($orderIds: [MongoDbObjectId!]!) {
        batchProductionFinishedPreview(orderIds: $orderIds) {
            eligible {
                _id
                orderId
            }
            skipped {
                _id
                orderId
                reason
            }
        }
    }
`;

// DD-3592: the lab's delivery-note batches of the last 24 hours, for re-download
// and re-running failed orders. downloadUrl is a short-lived signed URL,
// re-signed by the backend on every poll.
export const getRecentDeliveryNoteBatchesTemplate = gql`
    query recentDeliveryNoteBatches {
        recentDeliveryNoteBatches {
            _id
            action
            isRerun
            createdAt
            completedAt
            status
            total
            completedOrders {
                order
                orderId
            }
            failedOrders {
                order
                orderId
                errorMessage
            }
            downloadUrl
        }
    }
`;
